import { MetaService } from '../marketing/meta-service';
import type { TufirmaEvent, TrackingOptions } from './analytics-events';
import { getAnalyticsContext } from './analytics-data-collector';
import { generateUUID } from '../utils/uuid';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

/**
 * 💎 ANALYTICS SERVICE: The Sales Machine Edition.
 */
export const AnalyticsService = {
  identify(userId: string, traits: Record<string, any> = {}) {
    if (typeof window !== 'undefined') {
      posthog.identify(userId, traits);
    }
    Sentry.setUser({ id: userId, ...traits });
  },

  setGlobalContext(properties: Record<string, any>) {
    if (typeof window !== 'undefined') {
      posthog.register(properties);
    }
  },

  /**
   * Universal Tracking with Sales Focus.
   */
  async track(eventName: TufirmaEvent, options: TrackingOptions = {}) {
    const { 
      targets = ['posthog'], 
      properties = {}, 
      salonId,
      userData,
      customData 
    } = options;

    const eventId = options.eventId || generateUUID();
    const contextData = getAnalyticsContext(salonId);

    const phDistinctId = (typeof window !== 'undefined') 
      ? posthog.get_distinct_id?.() 
      : undefined;
    
    const distinctId = phDistinctId || salonId || contextData.sessionId || 'anonymous';
    
    const enrichedProperties = {
      ...contextData,
      ...properties,
      distinct_id: distinctId,
      eventName,
      eventCategory: this.getEventCategory(eventName),
      $insert_id: eventId, 
    };

    // 1. POSTHOG CLIENT
    if (targets.includes('posthog') && typeof window !== 'undefined') {
      posthog.capture(eventName, enrichedProperties);
    }

    // 2. META (CAPI + Pixel)
    if (targets.includes('meta')) {
      const metaEventName = customData?.eventName || eventName;

      const payload = {
        salonId: salonId || 'global',
        eventName: metaEventName as any,
        eventId,
        eventSourceUrl: typeof window === 'undefined' 
          ? 'https://app.tufirma.pro' 
          : window.location.href,
        userData: {
          ...userData,
          fbp: contextData.fbp,
          fbc: contextData.fbc,
          external_id: userData?.external_id || (typeof window !== 'undefined' ? posthog.get_distinct_id?.() : undefined),
          ...(userData?.name && { name: userData.name }),
          ...(userData?.phone && { phone: userData.phone }),
          ...(userData?.email && { email: userData.email }),
        },
        customData: {
          ...customData,
          value: Number(properties.value || customData?.value || 0),
          currency: (properties.currency || customData?.currency || 'USD') as any,
          content_name: properties.contentName || customData?.content_name,
          content_ids: properties.content_ids || customData?.content_ids,
          contents: properties.contents || customData?.contents,
        },
      };

      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', eventName, payload.customData, { eventID: eventId });
      }

      if (typeof window !== 'undefined') {
        await MetaService.trackOnline(payload);
      } else {
        await MetaService.trackOffline(payload);
      }
    }

    // 3. SENTRY
    if (targets.includes('sentry_breadcrumb')) {
      Sentry.addBreadcrumb({
        category: 'analytics',
        message: `Event: ${eventName}`,
        data: { ...enrichedProperties, salonId },
        level: 'info',
      });
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Analytics] 📊 Event: ${eventName}`, { targets, eventId });
    }
  },

  getEventCategory(eventName: TufirmaEvent): string {
    if (['product_viewed', 'course_viewed', 'catalog_viewed'].includes(eventName as string)) {
      return 'sales_discovery';
    }
    if (['whatsapp_clicked', 'location_opened', 'social_clicked'].includes(eventName as string)) {
      return 'engagement';
    }
    if (['lead_captured', 'purchase_completed', 'receipt_uploaded'].includes(eventName as string)) {
      return 'conversion';
    }
    return 'interaction';
  },

  trackError(error: any, context: string) {
    console.error(`[Analytics] 🚨 Error in ${context}:`, error);
    try {
      Sentry.captureException(error, { extra: { context } });
    } catch (e) {}
  }
};
